import { cx } from "class-variance-authority"
import { clsx } from "clsx"
import { useId, useState } from "react"
import { useTheme } from "next-themes"
import {
  type ClassNamesConfig,
  type ClearIndicatorProps,
  components,
  type ControlProps,
  default as ReactSelect,
  type DropdownIndicatorProps,
  type GroupBase,
  type OptionProps,
  type Props,
  type StylesConfig,
} from "react-select"
import Async, { type AsyncProps } from "react-select/async"
import { type CreatableProps, default as ReactCreatableSelect } from "react-select/creatable"

import { Label } from "~/components/reusable/Label"
import { Tooltip } from "~/components/reusable/Tooltip"
import { type LocalUser } from "~/hooks/useUsers"
import { CaretUpDown, Check, MagnifyingGlass, X } from "@phosphor-icons/react"
import { getTailwindValue } from "~/utils/tailwind"

import ProductPlaceholderSmall from "~/images/image_placeholder_small.png"
import ProductPlaceholderSmallDark from "~/images/image_placeholder_small_dark.png"

import { usePermissionGate, type PermissionGateProps } from "./PermissionTooltip"

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  const color = getTailwindValue("--color-fg-muted")
  return (
    <components.DropdownIndicator {...props}>
      <CaretUpDown color={color} />
    </components.DropdownIndicator>
  )
}

const DropdownIndicatorSearchIcon = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <MagnifyingGlass className="h-4 w-4" />
    </components.DropdownIndicator>
  )
}

type CustomOptionProps = OptionProps<ReactSelectOption<any>> & {
  data: ReactSelectOption<any>
}

const NewOption = (props: CustomOptionProps) => {
  const label = (
    <label
      className={cx(
        { "text-fg-muted": props.isDisabled },
        "text-sm cursor-pointer relative flex-1",
      )}
    >
      {props.children}
      {props.data.description && (
        <span className="block paragraph-sm text-fg-muted">{props.data.description}</span>
      )}
    </label>
  )
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between group">
        {props.data.tooltip ? (
          <Tooltip
            title={props.data.tooltip}
            delayDuration={100}
            side="right"
            // react-select forces the menu to zIndex: 2147483647 (int32 max) on lines 174/181.
            // Match it so the tooltip can paint on top via DOM order (tooltip mounts on hover,
            // i.e. after the menu, so equal z-index lets the later portal win).
            className="z-[2147483647]"
          >
            {label}
          </Tooltip>
        ) : (
          label
        )}
        {props.isSelected && <Check className="ml-4 h-4 w-4" />}
      </div>
    </components.Option>
  )
}

const containerStyles = "w-full"
const controlStyles = {
  base: "text-fg-base bg-button-neutral shadow-button-neutral label-base rounded-full hover:bg-button-neutral-hover px-2.5 gap-1.5",
  sizeLg: "min-w-[300px]",
  focus: "shadow-button-neutral-focus",
  nonFocus: "",
  // `!` — the base fill is a background-color too, so without it CSS order decides the winner.
  transparent: "bg-button-transparent! hover:bg-button-transparent-hover! shadow-none",
  disabled: "bg-button-disabled! text-fg-disabled",
}
const placeholderStyles = {
  base: "text-fg-muted text-md truncate",
  left: "pr-1",
  disabled: "text-fg-disabled",
}
const selectInputStyles = ""
const valueContainerStyles = "gap-1.5"

const singleValueStyles = "text-md"
const multiValueStyles =
  "bg-badge-neutral-bg rounded-[6px] items-center py-0.5 pl-1.5 pr-1 gap-0.5 paragraph-sm text-badge-neutral-fg-text"

const multiValueLabelStyles = "text-md"
const multiValueRemoveStyles = "text-fg-muted hover:text-fg-base rounded-md"

// const clearIndicatorStyles = "text-fg-subtle rounded-md hover:text-fg-base"
// const indicatorsContainerStyles = "" // "pl-1 py-0 gap-1"
// const indicatorSeparatorStyles = "bg-subtle"

const dropdownIndicatorStyles = {
  base: "pl-1 text-fg-base rounded-md",
  disabled: "text-fg-disabled",
  left: "order-first",
}

const menuStyles = "p-1 mt-2 bg-base rounded-lg z-50 min-w-[150px] shadow-flyout"
const groupHeadingStyles = "mb-1 px-2 py-1.5 label-base"

const optionStyles = {
  base: "transition-colors px-2 py-1.5 mt-0.5 rounded-[8px] paragraph-base",
  selected: "",
  disabled: "text-fg-disabled bg-none cursor-not-allowed opacity-60",
}
const noOptionsMessageStyles = "text-fg-subtle p-2 paragraph-sm"

type ClassNamesProps = {
  isClearable: boolean | undefined
  isMulti: boolean | undefined
  left?: boolean
  minWidth?: number
  fullWidthMenu?: boolean
  indentGroups?: boolean
  size?: "base" | "sm"
  transparent?: boolean
}

export type ReactSelectOption<T> = {
  label: string
  value: T
  isDisabled?: boolean
  tooltip?: string
  description?: string
  isFixed?: boolean
}

// TODO: Fix some typescript generic magic to make this work.
const getStyles = (
  compact = false,
  isMulti = false,
  truncate = false,
  hasGroups = false,
  menuAlign: "left" | "right" = "left",
): StylesConfig<any> => {
  return {
    option: (base) => ({
      ...base,
    }),
    // TODO: Look at why this gives a type error on build.
    input: (base) => ({
      ...base,
    }),
    // On mobile, the label will truncate automatically, so we want to
    // override that behaviour.
    multiValueLabel: (base, props) => ({
      ...base,
      ...(props.data.isFixed
        ? {
            paddingRight: "2px",
          }
        : {}),
    }),
    control: (base) => ({
      ...base,
      ...(!isMulti ? { height: "2.25rem", minHeight: "2.25rem" } : {}),
      ...(compact ? { height: "28px", minHeight: "28px" } : { height: "28px", minHeight: "32px" }),
      transition: "none",
    }),
    menuPortal: ({ left, top, ...provided }) => ({
      ...provided,
      zIndex: 2147483647,
      top,
      left,
      pointerEvents: "auto",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 2147483647,
      // The menu portal wrapper is pinned to the control's left edge with the control's
      // width; a wider (w-max) menu therefore overflows to the right and can clip the
      // viewport. Anchoring the menu's right edge to the control instead makes it grow
      // leftward, keeping it on-screen when the control sits near the right edge.
      ...(menuAlign === "right" ? { right: 0, left: "auto" } : {}),
    }),
    menuList: (base) => ({
      ...base,
      paddingLeft: hasGroups ? "16px" : undefined,
      height: "fit-content",
      maxHeight: "400px",
    }),
    singleValue: (base) => ({
      ...base,
      ...(truncate
        ? {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }
        : {}),
    }),
    multiValue: (base) => ({
      ...base,
      flexShrink: 0,
      minWidth: 0,
      ...(truncate
        ? {
            maxWidth: "33.33%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }
        : {}),
    }),
    multiValueRemove(base, props) {
      return {
        ...(props.data.isFixed
          ? {
              ...base,
              display: "none",
            }
          : base),
      }
    },
    valueContainer: (base) => ({
      ...base,
      flexWrap: "nowrap",
      overflowX: truncate && !isMulti ? "hidden" : "auto",
      overflowY: "hidden",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      ...(truncate && !isMulti ? { gridTemplateColumns: "minmax(0, 1fr)" } : {}),
    }),
    indicatorSeparator: (base) => ({
      ...base,
      ...(compact ? { display: "none" } : {}),
    }),
    indicatorsContainer: (base) => ({
      ...base,
    }),
  }
}

// TODO: Fix some typescript generic magic to make this work.
const getClassNames = ({
  // isClearable,
  // isMulti,
  fullWidthMenu,
  indentGroups,
  minWidth,
  transparent,
}: ClassNamesProps): ClassNamesConfig<any> => {
  return {
    container: () => containerStyles,
    control: ({ isFocused, isDisabled }) =>
      clsx(
        isFocused ? controlStyles.focus : controlStyles.nonFocus,
        controlStyles.base,
        isDisabled && controlStyles.disabled,
        minWidth && `min-w-[${minWidth}px]`,
        transparent && controlStyles.transparent,
      ),
    placeholder: ({ isDisabled }) =>
      clsx(placeholderStyles.base, isDisabled && placeholderStyles.disabled),
    input: () => selectInputStyles,
    valueContainer: () => valueContainerStyles,
    singleValue: () => singleValueStyles,
    multiValue: () => multiValueStyles,
    multiValueLabel: () => multiValueLabelStyles,
    multiValueRemove: () => multiValueRemoveStyles,
    // indicatorsContainer: () => indicatorsContainerStyles,
    // clearIndicator: () => clearIndicatorStyles,
    // indicatorSeparator: () => (isClearable || isMulti ? indicatorSeparatorStyles : "hidden"),
    // indicatorSeparator: () => "hidden",
    dropdownIndicator: ({ isDisabled }) =>
      clsx(dropdownIndicatorStyles.base, isDisabled && dropdownIndicatorStyles.disabled),
    // react-select unmounts the menu on close, so only the entrance animates.
    menu: ({ placement }) =>
      clsx(
        menuStyles,
        "overlay-enter",
        placement === "top"
          ? "origin-bottom [--overlay-enter-y:4px]"
          : "origin-top [--overlay-enter-y:-4px]",
        !fullWidthMenu && "w-max!",
      ),
    groupHeading: () => clsx(groupHeadingStyles, indentGroups && "-ml-4"),
    option: ({ isSelected, isFocused, isDisabled }) =>
      clsx(
        isSelected && optionStyles.selected,
        isFocused && "bg-component-hover",
        isDisabled && optionStyles.disabled,
        optionStyles.base,
      ),
    noOptionsMessage: () => noOptionsMessageStyles,
  }
}

type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & {
  label?: string
  size?: "base" | "sm"
  fullWidthMenu?: boolean
  leftIcon?: React.ReactNode
  prefix?: string
  transparent?: boolean
  truncate?: boolean
  /** Anchor the dropdown menu's left or right edge to the control. Use "right" when the
   * control sits near the right viewport edge (e.g. inside a right-aligned popover) to
   * stop a wide menu from clipping off-screen. Defaults to "left". */
  menuAlign?: "left" | "right"
  /** Grouped options are indented 16px under their heading by default. Set false to let
   * options span the full menu width, with headings aligned to them. */
  indentGroups?: boolean
} & PermissionGateProps

// Custom Control to support leftIcon inside the select control
const CustomControl = (props: ControlProps<any, any>) => {
  // @ts-expect-error: leftIcon is injected via our SelectProps, not in react-select's Props
  const leftIcon = props.selectProps.leftIcon
  // @ts-expect-error: prefix is injected via our SelectProps, not in react-select's Props
  const prefix = props.selectProps.prefix

  return (
    <components.Control {...props}>
      {leftIcon && (
        <span
          className={clsx(
            "flex items-center text-fg-subtle",
            props.isDisabled && "!text-fg-disabled",
          )}
        >
          {leftIcon}
        </span>
      )}
      {prefix && props.hasValue && <span className="label-base mr-[-2px]">{prefix}</span>}
      {props.children}
    </components.Control>
  )
}

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <X className="h-4 w-4" />
    </components.ClearIndicator>
  )
}

const hasGroupedOptions = (options: readonly unknown[] | undefined): boolean => {
  if (!options || options.length === 0) return false
  const first = options[0] as { options?: unknown }
  return first && "options" in first && Array.isArray(first.options)
}

function Select<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  requires,
  permissionReason,
  allowedTitle,
  tooltipSide,
  wrapperClassName,
  ...props
}: SelectProps<Option, IsMulti, Group>) {
  // The root div is block-level, so the wrapper span carries full width too.
  const { blocked, gate } = usePermissionGate(
    { requires, permissionReason, allowedTitle, tooltipSide, wrapperClassName },
    "w-full",
  )
  const instanceId = useId()
  const size = props.size || "base"
  const select = (
    // onWheel to prevent parent scroll when scrolling the select options
    <div onWheel={(e) => e.stopPropagation()}>
      {props.label && (
        <div className="flex items-center h-[22px]">
          <Label>{props.label}</Label>
        </div>
      )}
      <ReactSelect
        unstyled
        // @ts-expect-error: react-select generic typing issue
        styles={{
          ...getStyles(
            size !== "base",
            props.isMulti,
            props.truncate,
            hasGroupedOptions(props.options) && props.indentGroups !== false,
            props.menuAlign,
          ),
        }}
        menuPortalTarget={document.body}
        // DO NOT REMOVE: This ensures we don't have infinite rerenders
        // when menuPosition is anything else than fixed, it will continuously recalculate position
        // the fixed position stops this from happening, but CAN cause issues if the select is inside
        // a container with scroll, so test thoroughly if you change this
        menuPosition="fixed"
        components={{
          // @ts-expect-error: react-select generic typing issue
          Option: NewOption,
          // @ts-expect-error: react-select generic typing issue
          DropdownIndicator: DropdownIndicator,
          Control: CustomControl,
          // @ts-expect-error: react-select generic typing issue
          ClearIndicator: ClearIndicator,
        }}
        // @ts-expect-error: react-select generic typing issue
        classNames={{
          ...getClassNames({
            isClearable: props.isClearable,
            isMulti: props.isMulti,
            transparent: props.transparent,
            fullWidthMenu: props.fullWidthMenu,
            indentGroups: hasGroupedOptions(props.options) && props.indentGroups !== false,
          }),
        }}
        instanceId={instanceId}
        {...props}
        isDisabled={props.isDisabled || blocked}
      />
    </div>
  )

  return gate(select)
}

type AsyncSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = AsyncProps<Option, IsMulti, Group> & {
  label?: string
  darkmode?: boolean
  left?: boolean
  size?: "base" | "sm"
  searchIcon?: boolean
  compact?: boolean
}

export function AsyncSelect<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AsyncSelectProps<Option, IsMulti, Group>) {
  const size = props.size || "base"
  // @ts-ignore
  return (
    <div>
      {props.label && (
        <div className="flex items-center h-[22px]">
          <Label>{props.label}</Label>
        </div>
      )}
      <Async
        unstyled
        cacheOptions
        // @ts-expect-error
        styles={{ ...getStyles(size !== "base", props.isMulti) }}
        components={{
          LoadingMessage: LoadingMessage,
          // @ts-expect-error
          Option: NewOption,
          // @ts-expect-error
          DropdownIndicator: props.searchIcon ? DropdownIndicatorSearchIcon : DropdownIndicator,
        }}
        menuPortalTarget={document.body}
        // DO NOT REMOVE: This ensures we don't have infinite rerenders
        // when menuPosition is anything else than fixed, it will continuously recalculate position
        // the fixed position stops this from happening, but CAN cause issues if the select is inside
        // a container with scroll, so test thoroughly if you change this
        menuPosition="fixed"
        // @ts-expect-error
        classNames={{
          ...getClassNames({
            isClearable: props.isClearable,
            isMulti: props.isMulti,
            size: props.size,
          }),
        }}
        {...props}
      />
    </div>
  )
}

type CreatableSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = CreatableProps<Option, IsMulti, Group> & {
  label?: string
  size?: "base" | "sm"
}

export function CreatableSelect<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: CreatableSelectProps<Option, IsMulti, Group>) {
  const size = props.size || "base"
  return (
    <div>
      {props.label && (
        <div className="flex items-center h-[22px]">
          <Label>{props.label}</Label>
        </div>
      )}
      <ReactCreatableSelect
        unstyled
        // @ts-expect-error
        styles={{ ...getStyles(size !== "base", props.isMulti) }}
        // @ts-expect-error
        components={{ Option: NewOption, DropdownIndicator }}
        // @ts-expect-error
        classNames={{
          ...getClassNames({ isClearable: props.isClearable, isMulti: props.isMulti }),
        }}
        {...props}
      />
    </div>
  )
}

export type UserOption = {
  label: string
  value: string
  picture: string
}

const UserOptionLabel: React.FC<UserOption> = ({ label, picture }) => {
  const { resolvedTheme } = useTheme()
  const [hasError, setHasError] = useState(false)

  const shouldShowPlaceholder = !picture || hasError
  const placeholderSrc =
    resolvedTheme === "dark" ? ProductPlaceholderSmallDark : ProductPlaceholderSmall

  return (
    <div className="flex items-center gap-2">
      {shouldShowPlaceholder ? (
        <img
          src={placeholderSrc}
          alt={label}
          className="w-4 h-4 rounded-full object-cover shadow-base"
        />
      ) : (
        <img
          src={picture}
          alt={label}
          onError={() => setHasError(true)}
          className="w-4 h-4 rounded-full object-cover shadow-base"
        />
      )}
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}

type UserSelectProps = SelectProps<UserOption> & {
  users: LocalUser[]
  selectedUser: LocalUser | undefined
  size?: "base" | "sm"
}

export const UserSelect: React.FC<UserSelectProps> = ({
  users,
  selectedUser,
  size = "base",
  requires,
  permissionReason,
  allowedTitle,
  tooltipSide,
  wrapperClassName,
  ...props
}) => {
  const { blocked, gate } = usePermissionGate(
    { requires, permissionReason, allowedTitle, tooltipSide, wrapperClassName },
    "w-full",
  )
  const instanceId = useId()
  const options: UserOption[] = users.map((user) => ({
    label: user.name,
    value: user.email,
    picture: user.picture,
  }))

  const selectedOption = selectedUser
    ? options.find((option) => option.value === selectedUser.email)
    : null

  const formatOptionLabel = (option: UserOption) => <UserOptionLabel {...option} />

  return gate(
    <div onWheel={(e) => e.stopPropagation()}>
      {props.label && (
        <div className="flex items-center h-[22px]">
          <Label>{props.label}</Label>
        </div>
      )}
      <ReactSelect
        unstyled
        styles={{ ...getStyles(size !== "base", props.isMulti) }}
        classNames={{
          ...getClassNames({
            isClearable: props.isClearable,
            isMulti: props.isMulti,
          }),
        }}
        options={options}
        value={selectedOption}
        formatOptionLabel={formatOptionLabel}
        instanceId={instanceId}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        {...props}
        isDisabled={props.isDisabled || blocked}
      />
    </div>,
  )
}

const LoadingMessage = () => {
  return <div className="text-md text-fg-muted p-1 text-center">Loading...</div>
}

type UserAsyncSelectProps<
  IsMulti extends boolean = false,
  Group extends GroupBase<UserOption> = GroupBase<UserOption>,
> = AsyncSelectProps<UserOption, IsMulti, Group> & PermissionGateProps

export function UserAsyncSelect<
  IsMulti extends boolean = false,
  Group extends GroupBase<UserOption> = GroupBase<UserOption>,
>({
  requires,
  permissionReason,
  allowedTitle,
  tooltipSide,
  wrapperClassName,
  ...props
}: UserAsyncSelectProps<IsMulti, Group>) {
  const size = props.size || "base"
  const { blocked, gate } = usePermissionGate(
    { requires, permissionReason, allowedTitle, tooltipSide, wrapperClassName },
    "w-full",
  )

  const formatOptionLabel = (option: UserOption) => <UserOptionLabel {...option} />

  return gate(
    <div>
      {props.label && (
        <div className="flex items-center h-[22px]">
          <Label>{props.label}</Label>
        </div>
      )}
      <Async
        unstyled
        cacheOptions
        // @ts-expect-error
        styles={{ ...getStyles(size !== "base", props.isMulti) }}
        formatOptionLabel={formatOptionLabel}
        components={{
          LoadingMessage: LoadingMessage,
          // @ts-expect-error
          DropdownIndicator: props.searchIcon ? DropdownIndicatorSearchIcon : DropdownIndicator,
        }}
        // @ts-expect-error
        classNames={{
          ...getClassNames({
            isClearable: props.isClearable,
            isMulti: props.isMulti,
          }),
        }}
        {...props}
        isDisabled={props.isDisabled || blocked}
      />
    </div>,
  )
}

export default Select
